import { supabase } from '@/integrations/supabase/client';
import { authService } from '@/services/authService';
import { track, identifyUser, resetAnalytics, EVENTOS } from '@/lib/analytics';
import { Profile } from '@/types/common';
import { Session, User } from '@supabase/supabase-js';
import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { marcarAceitacaoNoRegisto } from '@/lib/consentimentoRegisto';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, userData: Partial<Profile>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  createPin: (pin: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithFacebook: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Limpeza local robusta após logout (independente de rede/CORS)
  const cleanupLocalAuthState = () => {
    try {
      // Remover sessão persistida do supabase (chave personalizada do projeto)
      localStorage.removeItem('tamoaki_clientes_auth');
      // Remover possíveis artefatos da sessão do prestador
      localStorage.removeItem('prestador_token');
      localStorage.removeItem('prestador_data');
      // Limpar caches de dados principais usados na UI
      localStorage.removeItem('categories-cache');
      localStorage.removeItem('subscription-cache');
    } catch (err) {
      // Ignorar erros de acesso ao storage
      console.warn('⚠️ Falha ao limpar storage durante logout:', err);
    }
  };

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error);

        // Fallback: criar perfil mínimo se não existir (após login via Google)
        if ((error as any)?.code === 'PGRST116' || (error as any)?.details?.includes('Results contain 0 rows')) {
          try {
            const { data: userRes } = await supabase.auth.getUser();
            const authUser = userRes?.user;
            if (!authUser) return;

            // Tentar via RPC se existir (bypassa RLS quando configurado no servidor)
            const { error: rpcError } = await supabase.rpc('create_user_profile', {
              user_id: authUser.id,
              profile_data: {
                id: authUser.id,
                full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'Utilizador',
                phone: authUser.user_metadata?.phone || '',
                avatar_url: authUser.user_metadata?.avatar_url || null,
                endereco_completo: '',
                // 26/10/2026: era true sem a pessoa ter aceitado nada; a
                // aceitação é pedida pelo AceitarPoliticas (consentimentos).
                aceitar_termos: false,
                aceitar_privacidade: false,
                verificado: false,
                idioma_preferido: 'pt',
                receber_notificacoes: true
              }
            });

            if (rpcError) {
              // Caso a RPC não exista, tentar insert direto respeitando RLS
              const { data: insertData, error: insertError } = await supabase
                .from('profiles')
                .insert([{ id: authUser.id, full_name: authUser.user_metadata?.full_name || 'Utilizador', phone: authUser.user_metadata?.phone || '' }])
                .select()
                .single();

              if (insertError) {
                console.error('Falha ao criar perfil automaticamente:', insertError);
                return;
              }

              setProfile(insertData as Profile);
              return;
            }

            // Se RPC criou com sucesso, buscar novamente
            const { data: createdProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', authUser.id)
              .single();
            if (createdProfile) setProfile(createdProfile as Profile);
            return;
          } catch (e) {
            console.error('Erro no fallback de criação de perfil:', e);
            return;
          }
        }
        return;
      }

      setProfile(data as Profile);
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 AuthProvider signIn chamado para:', email)
      const result = await authService.signIn({ email, password });
      
      console.log('📋 AuthProvider signIn resultado:', result)
      
      if (result.error) {
        console.error('❌ AuthProvider signIn erro:', result.error)
        return { error: new Error(result.error) };
      }

      if (result.user) {
        console.log('✅ AuthProvider signIn sucesso, definindo usuário e buscando perfil...')
        setUser(result.user as User);

        // Buscar perfil da tabela profiles
        await fetchProfile(result.user.id);
      }

      return { error: null };
    } catch (error) {
      console.error('💥 AuthProvider signIn erro inesperado:', error)
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      console.log('🔐 AuthProvider signUp chamado com:', { email, hasPassword: !!password, userData })
      
      const result = await authService.signUp({
        email,
        password,
        full_name: userData.full_name || '',
        phone: userData.phone || '',
        gender: userData.gender,
        is_prestador: userData.is_prestador,
        endereco_completo: userData.endereco_completo,
        provincia_id: userData.provincia_id,
        municipio_id: userData.municipio_id,
        distrito_id: userData.distrito_id,
        bi_nif: userData.bi_nif,
        tipo_documento: userData.tipo_documento,
        categoria_id: userData.categoria_id,
        subcategoria_id: userData.subcategoria_id,
        tipo_servico: userData.tipo_servico,
        anos_experiencia: userData.anos_experiencia,
        descricao_profissional: userData.descricao_profissional,
        plano_subscricao_id: userData.plano_subscricao_id,
        metodo_pagamento: userData.metodo_pagamento,
        dados_bancarios: userData.dados_bancarios,
        acceptedTerms: userData.acceptedTerms,
        acceptedPrivacy: userData.acceptedPrivacy
      });
      
      console.log('✅ AuthProvider signUp resultado:', result)
      
      if (result.error) {
        return { error: new Error(result.error) };
      }

      // Vistos do registo: registados com a versão no 1.º acesso com sessão.
      if (userData.acceptedTerms === true && userData.acceptedPrivacy === true) marcarAceitacaoNoRegisto();

      if (result.user) {
        setUser(result.user as User);
        setProfile(result.user as User);
        // Funil: topo — cliente registado.
        const u = result.user as User;
        identifyUser(u.id, { is_prestador: !!userData.is_prestador });
        track(EVENTOS.registo, { is_prestador: !!userData.is_prestador });
        track(EVENTOS.userRegistered, { role: userData.is_prestador ? 'provider' : 'customer' });
        if (!userData.is_prestador) track(EVENTOS.customerRegistered);
      }

      return { error: null };
    } catch (error) {
      console.error('💥 AuthProvider signUp erro:', error)
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    } catch (err) {
      // Mesmo se a chamada remota falhar, garantimos a limpeza local
      console.warn('⚠️ signOut remoto falhou, aplicando limpeza local mesmo assim:', err);
    } finally {
      resetAnalytics();
      cleanupLocalAuthState();
      setUser(null);
      setSession(null);
      setProfile(null);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      return { error: new Error('Usuário não autenticado') };
    }

    try {
      const result = await authService.updateProfile(user.id, updates);
      
      if (result.error) {
        return { error: new Error(result.error) };
      }

      if (result.user) {
        setProfile(result.user as User);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const createPin = async (pin: string) => {
    if (!user) return { error: new Error('Usuário não autenticado') };

    try {
      // Usar função RPC para criar PIN com hash seguro
      const { data, error } = await supabase.rpc('create_user_pin', {
        p_user_id: user.id,
        p_pin: pin
      });

      if (error) {
        console.error('Erro na função RPC:', error);
        return { error: new Error('Erro ao criar PIN') };
      }

      if (data && !data.success) {
        return { error: new Error(data.error || 'Erro ao criar PIN') };
      }

      return { error: null };
    } catch (error) {
      console.error('Erro ao criar PIN:', error);
      return { error: new Error('Erro inesperado ao criar PIN') };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await authService.signInWithGoogle();
      return { error: error ? new Error(error) : null };
    } catch (error) {
      return { error: new Error(error.message || 'Erro inesperado no OAuth Google') };
    }
  };

  const signInWithFacebook = async () => {
    try {
      const { error } = await authService.signInWithFacebook();
      return { error: error ? new Error(error) : null };
    } catch (error) {
      return { error: new Error(error.message || 'Erro inesperado no OAuth Facebook') };
    }
  };

  useEffect(() => {
    // Obter sessão inicial — setLoading(false) logo após getSession() resolve,
    // sem esperar por fetchProfile (evita loading infinito se o perfil demorar)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false); // desbloquear UI imediatamente
      if (session?.user) {
        fetchProfile(session.user.id); // carregar perfil em paralelo
      }
    });

    // Escutar mudanças de auth (login, logout, refresh de token)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false); // garantir que loading sai sempre
      if (session?.user) {
        fetchProfile(session.user.id); // sem await — não bloquear
        if (event === 'SIGNED_IN') {
          identifyUser(session.user.id);
          track(EVENTOS.login, { method: session.user.app_metadata?.provider || 'email' });
        }
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
    createPin,
    signInWithGoogle,
    signInWithFacebook,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Exportar o contexto para uso no hook
export { AuthContext };
